import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Aviso } from '@/components/ui/aviso';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { adminService } from '../services/admin-service';
import type { Plan, ResultadoDeLaConversion } from '../types/admin.types';
import { SelectorDePlan } from './selector-de-plan';
import { planesParaConvertir } from '../lib/planes-de-prueba';
import { TEXTOS_DE_LA_CONVERSION as T } from '../lib/demo-textos';

interface Props {
  abierto: boolean;
  empresaId: string;
  nombre: string;
  onCerrar: () => void;
  /** La empresa ya es de plan completo: la ficha puede volver a leerla. */
  onConvertida: () => void;
  /** A (2-nov): los planes para elegir con cuál sigue. */
  planes: Plan[];
}

/** Por omisión, un año desde hoy (la plataforma lo cambia). */
const enUnAnio = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

/**
 * «Pasar al plan completo» (la ficha de una empresa en prueba). Dice ANTES qué pasa y, al terminar, qué hizo.
 * No tiene lógica aparte: usa la misma ruta de siempre (`adminService.convertirDemo`). Si el servidor lo
 * rechaza (por ejemplo, sin un plan asignado), su motivo queda a la vista en el mismo diálogo.
 */
export function ConvertirDemoDialog({
  abierto,
  empresaId,
  nombre,
  onCerrar,
  onConvertida,
  planes,
}: Props) {
  const { toast } = useToast();
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<ResultadoDeLaConversion | null>(null);
  const [planId, setPlanId] = useState('');
  const [hasta, setHasta] = useState(enUnAnio);

  // Al abrir se parte de cero: ni el error ni el resultado de otra vez.
  const estabaAbierto = useRef(false);
  useEffect(() => {
    if (abierto && !estabaAbierto.current) {
      setError(null);
      setResultado(null);
      setPlanId('');
      setHasta(enUnAnio());
    }
    estabaAbierto.current = abierto;
  }, [abierto]);

  const listo = !!planId && !!hasta;
  const convertir = async () => {
    if (!listo) {
      setError(T.faltaPlan);
      return;
    }
    setTrabajando(true);
    setError(null);
    try {
      const r = await adminService.convertirDemo(empresaId, {
        planId,
        // Fin del día elegido: «hasta el 2 de noviembre» incluye ese día.
        endDate: new Date(`${hasta}T23:59:59`).toISOString(),
      });
      setResultado(r);
      toast({ title: T.hecho(nombre) });
      onConvertida();
    } catch (err) {
      setError(mensajeDeError(err, T.noSePudo));
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && !trabajando && onCerrar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{resultado ? T.hecho(nombre) : T.titulo(nombre)}</DialogTitle>
          {!resultado && <DialogDescription>{T.intro(nombre)}</DialogDescription>}
        </DialogHeader>

        {resultado ? (
          <Aviso tono="ok">
            {resultado.plan ? `${T.conPlan(resultado.plan.nombre)} ` : ''}
            {resultado.estrategiasPasadasASupervisadas.length === 0
              ? T.sinEstrategias
              : T.estrategias(resultado.estrategiasPasadasASupervisadas.map((e) => e.name))}
          </Aviso>
        ) : (
          <div className="space-y-3 py-2">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <span className="mb-1 block text-sm font-medium">{T.plan}</span>
                <SelectorDePlan
                  planes={planesParaConvertir(planes)}
                  valor={planId}
                  onCambio={setPlanId}
                />
              </div>
              <label className="block text-sm font-medium">
                <span className="mb-1 block">{T.hasta}</span>
                <Input
                  type="date"
                  value={hasta}
                  onChange={(e) => setHasta(e.target.value)}
                  aria-label={T.hasta}
                />
              </label>
            </div>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {T.quePasa.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
            {error && <Aviso tono="alerta">{error}</Aviso>}
          </div>
        )}

        <DialogFooter>
          {resultado ? (
            <Button onClick={onCerrar}>Listo</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onCerrar} disabled={trabajando}>
                {T.cancelar}
              </Button>
              <Button onClick={convertir} disabled={trabajando || !listo}>
                {trabajando ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {T.trabajando}
                  </>
                ) : (
                  T.confirmar
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
